import { gql, useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Search, 
  Box, 
  Cuboid, 
  ArrowLeft, 
  Braces, 
  Code2, 
  Link as LinkIcon, 
  FileJson,
  Activity,
  Server,
  Database
} from 'lucide-react';
import { cn } from '@/lib/utils';

const GET_GQL_SCHEMA = gql`
  query GetGqlSchema {
    getGqlSchema
  }
`;

type SchemaKind = 'query' | 'mutation' | 'subscription';

type Endpoint = {
  name: string;
  kind: SchemaKind;
  returnType?: string;
  parameters?: Record<string, unknown>;
};

type TypeEntry = {
  input?: boolean;
  fields?: Record<string, { type?: string }>;
  foreignFields?: Record<string, { type?: string }>;
  description?: string;
};

type SchemaPayload = {
  endpoints?: Endpoint[];
  types?: Record<string, TypeEntry>;
};

type TypeItem = {
  name: string;
  entry: TypeEntry;
};

type TypeFilter = 'all' | 'input' | 'output';

const parseSchemaPayload = (raw?: string | null): SchemaPayload => {
  if (!raw) {
    return { endpoints: [], types: {} };
  }
  try {
    const parsed = JSON.parse(raw);
    const endpoints = Array.isArray(parsed?.endpoints) ? parsed.endpoints : [];
    const types = parsed?.types && typeof parsed.types === 'object' ? parsed.types : {};
    return { endpoints, types };
  } catch (error) {
    return { endpoints: [], types: {} };
  }
};

const normalizeTypeName = (value: string) => `${value || ''}`.replace(/[!\[\]]/g, '').trim();

const toTypeName = (value: unknown) => {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return normalizeTypeName(value);
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record.type === 'string') {
      return normalizeTypeName(record.type);
    }
  }
  return '';
};

const toDomId = (prefix: string, value: string) => {
  const normalized = `${value || ''}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${prefix}-${normalized || 'item'}`;
};

export const GqlTypesPage = () => {
  const { data, loading, error } = useQuery<{ getGqlSchema: string }>(GET_GQL_SCHEMA);
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [filter, setFilter] = useState<TypeFilter>('all');

  const payload = useMemo(() => parseSchemaPayload(data?.getGqlSchema), [data?.getGqlSchema]);
  const typesMap = payload.types || {};
  const endpoints = payload.endpoints || [];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextSearch = `${params.get('search') || ''}`.trim();
    const nextType = `${params.get('type') || ''}`.trim();
    if (nextSearch !== searchTerm) {
      setSearchTerm(nextSearch);
    }
    if (nextType && nextType !== selectedType) {
      setSelectedType(nextType);
    }
  }, [location.search, searchTerm, selectedType]);

  const usageByType = useMemo(() => {
    const map = new Map<string, Endpoint[]>();
    endpoints.forEach((endpoint) => {
      const typeNames = new Set<string>();
      const returnType = toTypeName(endpoint.returnType);
      if (returnType) {
        typeNames.add(returnType);
      }
      if (endpoint.parameters) {
        Object.values(endpoint.parameters).forEach((value) => {
          const paramType = toTypeName(value);
          if (!paramType) {
            return;
          }
          typeNames.add(paramType);
        });
      }
      typeNames.forEach((typeName) => {
        const list = map.get(typeName) || [];
        list.push(endpoint);
        map.set(typeName, list);
      });
    });
    map.forEach((list, key) => {
      map.set(
        key,
        [...list].sort((a, b) => a.name.localeCompare(b.name))
      );
    });
    return map;
  }, [endpoints]);

  const typesList = useMemo(() => {
    const entries = Object.entries(typesMap).map(([name, entry]) => ({ name, entry }));
    let filtered = entries;
    if (filter === 'input') {
      filtered = filtered.filter((item) => item.entry.input);
    }
    if (filter === 'output') {
      filtered = filtered.filter((item) => !item.entry.input);
    }
    const trimmed = searchTerm.trim().toLowerCase();
    if (!trimmed) {
      return filtered.sort((a, b) => a.name.localeCompare(b.name));
    }
    return filtered
      .filter((item) => item.name.toLowerCase().includes(trimmed))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [typesMap, searchTerm, filter]);

  const typesByName = useMemo(() => {
    return new Map(typesList.map((item) => [item.name, item.entry]));
  }, [typesList]);

  useEffect(() => {
    if (selectedType && typesByName.has(selectedType)) {
      return;
    }
    if (typesList.length > 0 && !selectedType) {
      // Don't auto-select first item if user hasn't interacted, 
      // but if we navigated here with a type, it's handled above.
      // If we just loaded the page without params, maybe auto-select first is fine.
      setSelectedType(typesList[0].name);
    }
  }, [selectedType, typesByName, typesList]);

  const selectedEntry = selectedType ? typesByName.get(selectedType) : undefined;

  const selectedFields = useMemo(() => {
    if (!selectedEntry) {
      return [] as Array<[string, { type?: string }]>;
    }
    const merged = {
      ...(selectedEntry.fields || {}),
      ...(selectedEntry.foreignFields || {})
    } as Record<string, { type?: string }>;
    return Object.entries(merged).sort((a, b) => a[0].localeCompare(b[0]));
  }, [selectedEntry]);

  const fieldTypeNames = useMemo(() => {
    const names = new Set<string>();
    selectedFields.forEach(([_, fieldDef]) => {
      const typeName = toTypeName(fieldDef?.type);
      if (typeName) {
        names.add(typeName);
      }
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [selectedFields]);

  const usageList = useMemo(() => {
    if (!selectedType) {
      return [] as Endpoint[];
    }
    return usageByType.get(selectedType) || [];
  }, [selectedType, usageByType]);

  const inputCount = useMemo(() => typesList.filter((item) => item.entry.input).length, [typesList]);
  const outputCount = useMemo(
    () => typesList.filter((item) => !item.entry.input).length,
    [typesList]
  );

  if (loading) {
    return (
      <div className="flex h-[50vh] w-full items-center justify-center bg-slate-50/30">
        <div className="flex flex-col items-center gap-4 text-slate-500">
          <Activity className="h-8 w-8 animate-pulse text-indigo-600" />
          <p className="text-sm font-medium">Loading type definitions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-8">
        <div className="flex flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <Server className="h-10 w-10 text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-red-900">Unable to load schema</h3>
          <p className="text-sm text-red-700">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-slate-50">
      {/* Header */}
      <div className="flex h-16 items-center border-b border-slate-200 bg-white px-6 shadow-sm">
         <Link
           to="/admin/dev"
           id="gql-types-back-link"
           className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors mr-6"
         >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm font-medium">Back</span>
         </Link>
         <div className="flex h-6 w-px bg-slate-200 mr-6" />
         
         <div className="flex items-center gap-2 mr-auto">
            <h1 className="text-lg font-semibold text-slate-900">Type System</h1>
            <Badge variant="secondary" className="bg-slate-100 text-slate-600 border-slate-200">
                {typesList.length} Types
            </Badge>
         </div>

         <div className="relative max-w-md w-full">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              id="gql-types-search-input"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search types..."
              className="pl-9 bg-slate-50 border-slate-200 focus:bg-white transition-all"
            />
         </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Types List */}
        <div className="w-80 flex-shrink-0 border-r border-slate-200 bg-slate-50 flex flex-col">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <div className="flex gap-1 p-1 bg-slate-200 rounded-md">
                {(['all', 'input', 'output'] as const).map((key) => (
                  <button
                    key={key}
                    id={`gql-types-filter-${key}-button`}
                    onClick={() => setFilter(key as TypeFilter)}
                    className={cn(
                        "flex-1 flex items-center justify-center py-1.5 rounded text-xs font-medium transition-all capitalize",
                        filter === key
                          ? "bg-white text-slate-900 shadow-sm" 
                          : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50"
                    )}
                  >
                     {key}
                  </button>
                ))}
            </div>
          </div>
          
          <ScrollArea className="flex-1 px-3 py-4">
             {typesList.length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-8">No types found</div>
             ) : (
                <div className="space-y-1">
                   {typesList.map((item) => {
                      const isSelected = selectedType === item.name;
                      const fieldCount =
                        Object.keys(item.entry.fields || {}).length +
                        Object.keys(item.entry.foreignFields || {}).length;
                      
                      return (
                        <button
                          type="button"
                          key={item.name}
                          id={toDomId('gql-types-item', item.name)}
                          onClick={() => setSelectedType(item.name)}
                          className={cn(
                            "group flex w-full flex-col gap-1 px-3 py-2.5 rounded-lg text-left transition-all border border-transparent",
                            isSelected
                              ? "bg-white border-slate-200 shadow-sm"
                              : "hover:bg-slate-200/50 hover:border-slate-200/50"
                          )}
                        >
                           <div className="flex items-center justify-between">
                              <span className={cn("font-medium text-sm truncate", isSelected ? "text-indigo-600" : "text-slate-700")}>
                                {item.name}
                              </span>
                              {item.entry.input && <div className="h-1.5 w-1.5 rounded-full bg-amber-400" title="Input Type" />}
                           </div>
                           <div className="flex items-center justify-between text-xs text-slate-400">
                              <span>{fieldCount} Fields</span>
                           </div>
                        </button>
                      );
                   })}
                </div>
             )}
          </ScrollArea>
        </div>

        {/* Main Content - Type Details */}
        <div className="flex-1 bg-white flex flex-col overflow-hidden">
            {!selectedEntry ? (
               <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <Box className="h-16 w-16 mb-4 opacity-20" />
                  <p>Select a type to view definition</p>
               </div>
            ) : (
               <>
                  <div className="border-b border-slate-200 px-8 py-6 bg-white">
                      <div className="flex items-start justify-between">
                         <div>
                            <div className="flex items-center gap-3 mb-2">
                               <h2 className="text-2xl font-bold text-slate-900">{selectedType}</h2>
                               <Badge variant={selectedEntry.input ? "outline" : "secondary"} className={cn(
                                   "text-xs font-normal",
                                   selectedEntry.input ? "border-amber-200 bg-amber-50 text-amber-700" : "bg-indigo-50 text-indigo-700"
                               )}>
                                   {selectedEntry.input ? "Input Object" : "Object Type"}
                               </Badge>
                            </div>
                            <p className="text-slate-500 max-w-2xl">
                                {selectedEntry.description || "No description available for this type definition."}
                            </p>
                         </div>
                      </div>
                  </div>

                  <ScrollArea className="flex-1">
                      <div className="max-w-5xl mx-auto p-8 space-y-8">
                          
                          {/* Fields Table */}
                          <section>
                              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                                  <Braces className="h-4 w-4 text-slate-400" /> Fields
                              </h3>
                              {selectedFields.length === 0 ? (
                                  <div className="text-sm text-slate-400 italic px-4">No fields defined.</div>
                              ) : (
                                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                                      <table className="w-full text-sm text-left">
                                          <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-semibold">
                                              <tr>
                                                  <th className="px-6 py-3 border-b border-slate-200">Field Name</th>
                                                  <th className="px-6 py-3 border-b border-slate-200">Type Definition</th>
                                              </tr>
                                          </thead>
                                          <tbody className="divide-y divide-slate-100">
                                              {selectedFields.map(([fieldName, fieldDef]) => {
                                                  const fieldType = fieldDef?.type || '—';
                                                  const typeName = toTypeName(fieldType);
                                                  return (
                                                      <tr key={fieldName} className="hover:bg-slate-50/50">
                                                          <td className="px-6 py-4 font-medium text-slate-900">{fieldName}</td>
                                                          <td className="px-6 py-4 font-mono text-slate-600">
                                                              {typeName ? (
                                                                  <Link
                                                                      id={toDomId('gql-types-field-type', `${selectedType || 'type'}-${fieldName}`)}
                                                                      to={`/admin/dev/types?type=${encodeURIComponent(typeName)}`}
                                                                      className="text-indigo-600 hover:underline inline-flex items-center gap-1.5"
                                                                  >
                                                                      <Box className="h-3.5 w-3.5 opacity-50" />
                                                                      {fieldType}
                                                                  </Link>
                                                              ) : (
                                                                  <span className="text-slate-500">{fieldType}</span>
                                                              )}
                                                          </td>
                                                      </tr>
                                                  );
                                              })}
                                          </tbody>
                                      </table>
                                  </div>
                              )}
                          </section>

                          <div className="grid gap-8 md:grid-cols-2">
                              {/* Field Types */}
                              <section>
                                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                                      <Cuboid className="h-4 w-4 text-slate-400" /> Dependencies
                                  </h3>
                                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 h-full">
                                      {fieldTypeNames.length === 0 ? (
                                          <div className="text-sm text-slate-400">No dependencies.</div>
                                      ) : (
                                          <div className="flex flex-wrap gap-2">
                                              {fieldTypeNames.map((typeName) => (
                                                  <Link
                                                      key={typeName}
                                                      id={toDomId('gql-types-dependency-link', typeName)}
                                                      to={`/admin/dev/types?type=${encodeURIComponent(typeName)}`}
                                                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-xs font-medium text-slate-700 hover:text-indigo-600 hover:border-indigo-300 transition-colors"
                                                  >
                                                      <Box className="h-3 w-3 text-slate-400" />
                                                      {typeName}
                                                  </Link>
                                              ))}
                                          </div>
                                      )}
                                  </div>
                              </section>

                              {/* Usage References */}
                              <section>
                                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                                      <LinkIcon className="h-4 w-4 text-slate-400" /> Referenced By
                                  </h3>
                                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 h-full">
                                      {usageList.length === 0 ? (
                                          <div className="text-sm text-slate-400">No direct usage found in operations.</div>
                                      ) : (
                                          <ScrollArea className="h-48">
                                              <div className="space-y-2">
                                                  {usageList.map((endpoint) => (
                                                      <Link
                                                          key={`${endpoint.kind}:${endpoint.name}`}
                                                          id={toDomId('gql-types-referenced-by', endpoint.name)}
                                                          to={`/admin/dev/guide?focus=${encodeURIComponent(endpoint.name)}`}
                                                          className="flex items-center gap-2 p-2 rounded bg-white border border-slate-200 hover:border-blue-300 transition-colors group"
                                                      >
                                                          <div className={cn("p-1 rounded", 
                                                              endpoint.kind === 'query' ? "bg-emerald-50 text-emerald-600" : 
                                                              endpoint.kind === 'mutation' ? "bg-amber-50 text-amber-600" : "bg-purple-50 text-purple-600"
                                                          )}>
                                                              {endpoint.kind === 'query' && <Database className="h-3 w-3" />}
                                                              {endpoint.kind === 'mutation' && <Server className="h-3 w-3" />}
                                                              {endpoint.kind === 'subscription' && <Activity className="h-3 w-3" />}
                                                          </div>
                                                          <span className="text-xs font-medium text-slate-700 group-hover:text-blue-700">
                                                              {endpoint.name}
                                                          </span>
                                                      </Link>
                                                  ))}
                                              </div>
                                          </ScrollArea>
                                      )}
                                  </div>
                              </section>
                          </div>
                      </div>
                  </ScrollArea>
               </>
            )}
        </div>
      </div>
    </div>
  );
};
